import { useState, useCallback } from "react";
import {
  parseDailyWorkBulkTSV,
  validateDailyWorkBulkUpload,
  bulkUploadDailyWork,
  ParsedBulkRow,
} from "./bulk-upload.api";
import { store } from "@/lib/store";

export interface BulkUploadValidationState {
  isValid: boolean;
  errors: string[];
  rowCount: number;
}

export interface BulkUploadResultState {
  success: boolean;
  message: string;
  dailyWorksCreated?: number;
  dailyWorksReused?: number;
  dailyWorkDaysCreated?: number;
  errors?: string[];
}

export function useDailyWorkBulkUpload() {
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationState, setValidationState] = useState<BulkUploadValidationState | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedBulkRow[] | null>(null);
  const [uploadResult, setUploadResult] = useState<BulkUploadResultState | null>(null);

  const validateFile = useCallback(async (content: string) => {
    const parseResult = parseDailyWorkBulkTSV(content);
    if (parseResult.rows.length === 0 && parseResult.errors.length > 0) {
      setValidationState({
        isValid: false,
        errors: parseResult.errors,
        rowCount: 0,
      });
      setParsedRows([]);
      return;
    }

    setIsValidating(true);
    setUploadResult(null);
    try {
      const vendorOrgId = store.getState().organization.activeOrgId;
      if (!vendorOrgId) {
        setValidationState({
          isValid: false,
          errors: ["Not authenticated or organization not set."],
          rowCount: parseResult.rows.length,
        });
        setParsedRows(parseResult.rows);
        return;
      }

      const validation = await validateDailyWorkBulkUpload(parseResult.rows, vendorOrgId);
      setValidationState({
        isValid: validation.valid,
        errors: [...parseResult.errors, ...validation.errors],
        rowCount: parseResult.rows.length,
      });
      setParsedRows(parseResult.rows);
    } catch (error) {
      setValidationState({
        isValid: false,
        errors: [error instanceof Error ? error.message : "Validation failed"],
        rowCount: parseResult.rows.length,
      });
      setParsedRows(parseResult.rows);
    } finally {
      setIsValidating(false);
    }
  }, []);

  const upload = useCallback(async (): Promise<BulkUploadResultState | null> => {
    if (!parsedRows || !validationState?.isValid || validationState.errors.length > 0) return null;

    const vendorOrgId = store.getState().organization.activeOrgId;
    if (!vendorOrgId) {
      const res: BulkUploadResultState = {
        success: false,
        message: "Not authenticated or organization not set.",
      };
      setUploadResult(res);
      return res;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    try {
      setUploadProgress(30);
      const result = await bulkUploadDailyWork(parsedRows, vendorOrgId);
      setUploadProgress(100);
      const res: BulkUploadResultState = {
        success: result.errors.length === 0,
        message:
          result.errors.length > 0
            ? `Upload completed with errors.`
            : [
                result.dailyWorksCreated > 0 ? `Created ${result.dailyWorksCreated} daily work(s)` : null,
                result.dailyWorksReused > 0 ? `Reused ${result.dailyWorksReused} existing daily work(s)` : null,
                `${result.dailyWorkDaysCreated} day(s) added`,
              ]
                .filter(Boolean)
                .join(", ") + ".",
        dailyWorksCreated: result.dailyWorksCreated,
        dailyWorksReused: result.dailyWorksReused,
        dailyWorkDaysCreated: result.dailyWorkDaysCreated,
        errors: result.errors.length > 0 ? result.errors : undefined,
      };
      setUploadResult(res);
      return res;
    } catch (error) {
      const res: BulkUploadResultState = {
        success: false,
        message: error instanceof Error ? error.message : "Upload failed",
        errors: [error instanceof Error ? error.message : "Upload failed"],
      };
      setUploadResult(res);
      return res;
    } finally {
      setIsUploading(false);
    }
  }, [parsedRows, validationState]);

  const reset = useCallback(() => {
    setValidationState(null);
    setParsedRows(null);
    setUploadResult(null);
    setIsValidating(false);
    setIsUploading(false);
    setUploadProgress(0);
  }, []);

  return {
    validateFile,
    upload,
    reset,
    isValidating,
    isUploading,
    uploadProgress,
    validationState,
    parsedRows,
    uploadResult,
    canUpload: Boolean(parsedRows && validationState?.isValid && validationState.errors.length === 0),
  };
}
